/**
 * @file AddressGenerators.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-21
 * @description HID EEPROM 键盘灯光地址生成器（共享版）
 *
 * 与 CH585M 的 addressGenerators.ts 保持一致的命名和格式，
 * 区别在于将芯片常量参数化，各芯片传入自己的基础地址。
 */

import {
  CommonAddress,
  LevelAddress,
  NestedLevelAddress,
  DeepNestedLevelAddress,
} from '../../../types/protocol'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'
import type { LayoutKey } from '../../../types/manifest'
import { isValidRowCol as sharedIsValidRowCol } from '../../shared/feature-impls/keyboard-lighting/lighting'

export function toCommonAddress(realAddr: number): CommonAddress {
  return [
    realAddr & 0x000000ff,
    (realAddr & 0x0000ff00) >> 8,
    (realAddr & 0x00ff0000) >> 16,
    (realAddr & 0xff000000) >> 24,
  ]
}

export const genrateLevelAddress = (addressLevel: number[], offset: number): LevelAddress => {
  return addressLevel.map((level) => toCommonAddress(level + offset)) as LevelAddress
}

export const genrateStaticLightEffectPresetAddressName = (
  presetAddressBase: number,
  presetLevelArray: number[],
  offset: number,
): LevelAddress => {
  return presetLevelArray.map((level) => {
    return toCommonAddress(presetAddressBase + level + offset)
  }) as LevelAddress
}

export const genrateStaticLightEffectPresetAddressLevel = (
  presetAddressBase: number,
  presetLevelArray: number[],
  offset: number,
): LevelAddress => {
  return presetLevelArray.map((level) => {
    return toCommonAddress(presetAddressBase + level + 0x0c + offset)
  }) as LevelAddress
}

export const genrateStaticLightEffectAddressLevel = (
  effectAddressLevel: number[],
  offset: number,
): LevelAddress => {
  return effectAddressLevel.map((level) => {
    return toCommonAddress(level + offset)
  }) as LevelAddress
}

export const genrateStaticLightRGBAddressLevel = (
  effectAddressLevel: number[],
  columnCount: number,
  rowCount: number,
  offset: number = 0,
): DeepNestedLevelAddress => {
  const PIXEL_STEP = 4
  const ROW_STEP = columnCount * PIXEL_STEP

  return effectAddressLevel.map((lightBaseAddr) => {
    const rowData: NestedLevelAddress = []
    for (let row = 0; row < rowCount; row++) {
      const colData: LevelAddress = []
      const rowOffset = row * ROW_STEP
      for (let col = 0; col < columnCount; col++) {
        const colOffset = col * PIXEL_STEP
        colData.push(toCommonAddress(lightBaseAddr + offset + rowOffset + colOffset))
      }
      rowData.push(colData)
    }
    return rowData
  }) as DeepNestedLevelAddress
}

export const genrateDynamicLightEffectAddressLevel = (
  addressLevel: number[],
  dynamicLevelArray: number[],
  offset: number,
): NestedLevelAddress => {
  return addressLevel.map((configBaseAddr) => {
    return dynamicLevelArray.map((dynamicBaseAddr) => {
      return toCommonAddress(configBaseAddr + dynamicBaseAddr + offset)
    })
  }) as NestedLevelAddress
}

export const genrateSideLightEffectAddressLevel = (
  addressLevel: number[],
  sideLevelArray: number[],
  offset: number,
): NestedLevelAddress => {
  return addressLevel.map((configBaseAddr) => {
    return sideLevelArray.map((dynamicBaseAddr) => {
      return toCommonAddress(configBaseAddr + dynamicBaseAddr + offset)
    })
  }) as NestedLevelAddress
}

// ==================== 按键矩阵地址生成（与 CH585M 共享地址池） ====================

/** 按键矩阵列数 */
const COLUMN_COUNT = 22
/** 按键矩阵行数 */
const ROW_COUNT = 6

/** 行优先：index = row * columnCount + col（以 22 列为准） */
export function rowColToIndex(row: number, col: number): number {
  return row * COLUMN_COUNT + col
}

/** 将行优先 index 转为行列（以 22 列为准） */
export function indexToRowCol(index: number): { row: number; col: number } {
  return {
    row: Math.floor(index / COLUMN_COUNT),
    col: index % COLUMN_COUNT,
  }
}

/**
 * 校验按键行列是否在键盘矩阵范围内。
 * 委托给 shared/lighting.ts 中的通用 key-index-set 实现。
 */
export function isValidRowCol(row: number, col: number, layoutKeys: LayoutKey[][]): boolean {
  return sharedIsValidRowCol(row, col, COLUMN_COUNT, ROW_COUNT, layoutKeys)
}

/** 按键矩阵地址层（与 CH585M 地址相同） */
export const LAYER_ADDRESS_MAP = {
  WINDOWS_BASE_LAYER_ADDRESS: [0x10003000, 0x10008000, 0x1000d000, 0x10012000],
  WINDOWS_FN_LAYER_ADDRESS: [0x10004000, 0x10009000, 0x1000e000, 0x10013000],
  MAC_BASE_LAYER_ADDRESS: [0x10005000, 0x1000a000, 0x1000f000, 0x10014000],
  MAC_FN_LAYER_ADDRESS: [0x10006000, 0x1000b000, 0x10010000, 0x10015000],
}

/**
 * 生成Windows基础层键映射地址（4个配置层 × 6行 × 22列）
 * 对应R0-C0~R5-C21的6×22按键矩阵
 */
export const genrateWindowsBaseLayerAddress = (
  baseOffset: number = 0,
  type: keyof typeof LAYER_ADDRESS_MAP = 'WINDOWS_BASE_LAYER_ADDRESS',
): DeepNestedLevelAddress => {
  const COLUMN_STEP = 0x10
  const ROW_STEP = COLUMN_COUNT * COLUMN_STEP

  const layerData = LAYER_ADDRESS_MAP[type].map((layerBaseAddr) => {
    const rowData: NestedLevelAddress = []
    for (let row = 0; row < ROW_COUNT; row++) {
      const colData: LevelAddress = []
      const rowOffset = row * ROW_STEP
      for (let col = 0; col < COLUMN_COUNT; col++) {
        const colOffset = col * COLUMN_STEP
        const realAddr = layerBaseAddr + baseOffset + rowOffset + colOffset
        colData.push(toCommonAddress(realAddr))
      }
      rowData.push(colData)
    }
    return rowData
  })

  return layerData as DeepNestedLevelAddress
}

// ==================== 宏地址生成（与 CH585M 共享地址池） ====================

/** 宏读写区域基础地址 */
export const MACRO_PACKAGE_ADDRESS_LEVEL = 0x10020000
/** 宏名称区域基础地址 */
export const MACRO_NAME_ADDRESS_LEVEL = 0x10030000

/** 生成宏存储地址（128条宏，每条512字节） */
export const genrateMacroAddressLevel = (offset: number): LevelAddress => {
  const MACRO_COUNT = 128
  const MACRO_BYTE_SIZE = 512

  const data = Array.from({ length: MACRO_COUNT }, (_, index) => {
    const realAddr = MACRO_PACKAGE_ADDRESS_LEVEL + index * MACRO_BYTE_SIZE + offset
    return toCommonAddress(realAddr)
  })

  return data as LevelAddress
}

/** 生成宏名称地址（128个名称，每个16字节） */
export const genrateMacroNameAddressLevel = (offset: number): LevelAddress => {
  const MACRO_NAME_COUNT = 128
  const MACRO_NAME_BYTE_SIZE = 16

  const data = Array.from({ length: MACRO_NAME_COUNT }, (_, index) => {
    const realAddr = MACRO_NAME_ADDRESS_LEVEL + index * MACRO_NAME_BYTE_SIZE + offset
    return toCommonAddress(realAddr)
  })

  return data as LevelAddress
}

/** 生成宏分包地址 */
export const genrateMacroPackageAddressLevel = (
  offset: CommonAddress,
  macroTotalLength: number,
  maxPackageLength: number = 24,
  maxMacroCount: number = 512,
): LevelAddress => {
  if (macroTotalLength <= 0 || macroTotalLength > maxMacroCount) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `The length of the macro (${macroTotalLength}) exceeds the allowed range (1 to ${maxMacroCount}).`,
    )
  }
  if (maxPackageLength <= 0) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `The length of a single packet must be greater than 0.`,
    )
  }
  if (!Array.isArray(offset) || offset.length !== 4) {
    throw new DeviceError(
      DeviceErrorCode.INVALID_PARAM,
      `The offset must be a CommonAddress type (4-digit number array).`,
    )
  }

  const startAddr = (offset[3] << 24) | (offset[2] << 16) | (offset[1] << 8) | offset[0]

  const packageCount = Math.ceil(macroTotalLength / maxPackageLength)
  const packageAddresses: LevelAddress = []

  for (let i = 0; i < packageCount; i++) {
    const packageAddr = startAddr + i * maxPackageLength
    packageAddresses.push(toCommonAddress(packageAddr))
  }

  return packageAddresses
}
